import {useParams, Link} from 'react-router-dom';
import React, { useState, useEffect, useRef } from "react";
import { useActiveLeague } from '../hooks/useActiveLeague';

// Remove static data imports
// import { findObjectByKeyValue } from '../components/utils.js';
import { MatchPageNav } from "../components/match/MatchPageNav";
import { FieldBlock } from "../components/match/FieldBlock";
import { ListPlayers } from "../components/match/ListPlayers";
import { useGetMatchById } from '../api/matchesApi.js'; // Import the hook
import { useLiveScore } from '../hooks/useLiveScore';
import { useGetTeamPlayers } from '../api/playersApi.js'; // Import hook for fetching players
import formationsData from "../json-data/field-positions.json"; // Import formation data
import { supabase } from '../lib/supabaseClient'; // Import Supabase client
import ballIcon from '../assets/messages/ball.png'; // Import ball icon

// Removed imports for PlayerChart, TransferBlock, TeamDropdown, Popup as they don't seem used here
// Removed JSON data imports
// Removed imgField import as it doesn't seem used

const FRONTEND_FALLBACK_LOGO_ASSET = ballIcon;
const BACKEND_DEFAULT_TEAM_LOGO_PATH = '/uploads/teams/7b9a405a6a953b17e04a8f0107c3e5fd.jpg';

// Helper function to clean up and validate logo URLs
const getCleanLogoUrl = (logoUrl) => {
    if (!logoUrl || logoUrl.trim() === "" || logoUrl === "/uploads/teams/default-team-logo.png") {
        return BACKEND_DEFAULT_TEAM_LOGO_PATH;
    }
    if (logoUrl === FRONTEND_FALLBACK_LOGO_ASSET) {
        return logoUrl;
    }
    if (logoUrl.startsWith('/https://') || logoUrl.startsWith('/http://')) {
        return logoUrl.substring(1);
    }
    if (logoUrl.startsWith('https://') || logoUrl.startsWith('http://')) {
        return logoUrl;
    }
    if (logoUrl.startsWith('/uploads/')) {
        return logoUrl;
    }
    console.warn(`[getCleanLogoUrl] Unrecognized logo URL format: "${logoUrl}". Falling back to backend default.`);
    return BACKEND_DEFAULT_TEAM_LOGO_PATH;
};

// Helper function to extract file path from Supabase URL
const extractSupabaseFilePath = (url) => {
    if (!url || !url.includes('supabase.co')) return null;
    const match = url.match(/\/storage\/v1\/object\/public\/(.*)/);
    return match ? match[1] : null;
};

// Component for handling individual team logo with error tracking and authenticated loading
const TeamLogo = ({ logoUrl, teamName, className = "" }) => {
    const [currentSrc, setCurrentSrc] = useState(FRONTEND_FALLBACK_LOGO_ASSET);
    const [hasErrored, setHasErrored] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const errorCountRef = useRef(0);
    const blobUrlRef = useRef(null);

    useEffect(() => {
        return () => {
            if (blobUrlRef.current && blobUrlRef.current !== FRONTEND_FALLBACK_LOGO_ASSET && blobUrlRef.current.startsWith('blob:')) {
                URL.revokeObjectURL(blobUrlRef.current);
            }
        };
    }, []);

    useEffect(() => {
        setHasErrored(false);
        errorCountRef.current = 0;
        setIsLoading(false);

        const loadImage = async () => {
            if (!logoUrl) {
                setCurrentSrc(FRONTEND_FALLBACK_LOGO_ASSET);
                return;
            }

            const cleanUrl = getCleanLogoUrl(logoUrl);
            if (!cleanUrl.includes('supabase.co')) {
                setCurrentSrc(cleanUrl);
                return;
            }

            const filePath = extractSupabaseFilePath(cleanUrl);
            if (!filePath) {
                console.warn(`Could not extract file path from Supabase URL: ${cleanUrl}`);
                setCurrentSrc(FRONTEND_FALLBACK_LOGO_ASSET);
                return;
            }

            setIsLoading(true);
            
            try {
                const [bucketName, ...pathParts] = filePath.split('/');
                const fileName = pathParts.join('/');
                const { data, error } = await supabase.storage.from(bucketName).download(fileName);
                
                if (error) throw error;
                
                if (blobUrlRef.current && blobUrlRef.current !== FRONTEND_FALLBACK_LOGO_ASSET && blobUrlRef.current.startsWith('blob:')) {
                    URL.revokeObjectURL(blobUrlRef.current);
                }
                
                const blobUrl = URL.createObjectURL(data);
                blobUrlRef.current = blobUrl;
                setCurrentSrc(blobUrl);
                setIsLoading(false);
                
            } catch (error) {
                console.log(`Error loading ${teamName} logo from Supabase:`, error.message);
                setCurrentSrc(FRONTEND_FALLBACK_LOGO_ASSET);
                setHasErrored(true);
                setIsLoading(false);
            }
        };

        loadImage();
    }, [logoUrl, teamName]);

    const handleError = (e) => {
        errorCountRef.current += 1;
        if (errorCountRef.current > 1 || hasErrored) return;
        setHasErrored(true);
        setCurrentSrc(FRONTEND_FALLBACK_LOGO_ASSET);
    };

    if (isLoading) {
        return <img src={FRONTEND_FALLBACK_LOGO_ASSET} alt={`Loading ${teamName || 'team'} logo...`} className={`${className} loading-opacity`} style={{ opacity: 0.5 }} />;
    }

    return <img src={currentSrc} alt={teamName || 'Team logo'} className={className} onError={handleError} />;
};

function MatchLineups() {
    const { id } = useParams(); // Get match ID from URL
    const { activeLeague } = useActiveLeague();

    // Fetch match data using the hook
    const { data: matchData, isLoading: isLoadingMatch, error: errorMatch } = useGetMatchById(id);
    const isMatchFinished = ['completed', 'forfeited', 'cancelled'].includes(matchData?.status);
    const { liveScore } = useLiveScore(id, { isMatchFinished });

    // Fetch players for both teams, only enable when team IDs are available
    const { data: homePlayersData, isLoading: isLoadingHomePlayers, error: errorHomePlayers } = useGetTeamPlayers(
        matchData?.homeTeam?._id,
        {
            leagueId: activeLeague?._id,
            season: activeLeague?.season,
            enabled: !!matchData?.homeTeam?._id,
        }
    );
    const { data: awayPlayersData, isLoading: isLoadingAwayPlayers, error: errorAwayPlayers } = useGetTeamPlayers(
        matchData?.awayTeam?._id,
        {
            leagueId: activeLeague?._id,
            season: activeLeague?.season,
            enabled: !!matchData?.awayTeam?._id,
        }
    );

    // Combine loading states
    const combinedLoading = isLoadingMatch || isLoadingHomePlayers || isLoadingAwayPlayers;

    if (combinedLoading) {
        return <div>Loading match and player data...</div>;
    }

    // Handle error states
    if (errorMatch || errorHomePlayers || errorAwayPlayers) {
        console.error("Error fetching data:", { errorMatch, errorHomePlayers, errorAwayPlayers });
        return <div>Error loading match details or player data. Please try again later.</div>;
    }

    // Handle case where data is fetched but is potentially empty or missing critical parts
    if (!matchData || !homePlayersData?.data || !awayPlayersData?.data || !matchData.homeTeam?.teamsheet || !matchData.awayTeam?.teamsheet) {
         console.error("Incomplete data received:", { matchData, homePlayersData, awayPlayersData });
         return <div>Incomplete match data received. Cannot display lineups.</div>;
    }

    // Use the actual data structure from the API response
    const match = matchData;
    const teamOne = match.homeTeam;
    const teamTwo = match.awayTeam;
    const allTeamPlayersOne = homePlayersData.data; // Array of all player objects for team one
    const allTeamPlayersTwo = awayPlayersData.data; // Array of all player objects for team two

    // Find formation objects based on titles from teamsheets
    const formationOneInfo = formationsData.find(f => f.title === teamOne.teamsheet?.formation);
    const formationTwoInfo = formationsData.find(f => f.title === teamTwo.teamsheet?.formation);

    // ** NEW: Get players listed in the teamsheet's starting XI **
    const sheetPlayersOne = teamOne.teamsheet?.players || [];
    const sheetPlayersTwo = teamTwo.teamsheet?.players || [];

    // Helper to build a mapping of wyId -> unique formation slot id
    const buildSlotMap = (sheetPlayers, formationInfo) => {
        if (!formationInfo || !formationInfo.positions) return {};
        const availableSlots = [...formationInfo.positions];
        const map = {};

        sheetPlayers.forEach(sp => {
            // Find first slot that matches player position and is still free
            const slotIdx = availableSlots.findIndex(s => s.position === sp.position);
            if (slotIdx !== -1) {
                const slot = availableSlots.splice(slotIdx, 1)[0];
                map[sp.wyId] = slot.id; // store mapping
            }
        });
        return map;
    };

    const slotMapOne = buildSlotMap(sheetPlayersOne, formationOneInfo);
    const slotMapTwo = buildSlotMap(sheetPlayersTwo, formationTwoInfo);

    // ** NEW: Get substitutes listed in the teamsheet **
    const subsOne = teamOne.teamsheet?.subs || [];
    const subsTwo = teamTwo.teamsheet?.subs || [];

    // ** NEW: Prepare player data for ListPlayers (needs full roster + assigned position/status) **
    const listPlayersDataOne = allTeamPlayersOne.map(p => {
        const xiPlayer = sheetPlayersOne.find(sp => sp.wyId === p.wyId);
        const subPlayer = subsOne.find(sp => sp.wyId === p.wyId);
        const formationPlaceId = slotMapOne[p.wyId] ?? null;
        const subPosition = subPlayer ? subPlayer.position : null; // Store sub position if applicable
        const number = xiPlayer?.number || subPlayer?.number || p.number || null;

        return {
            ...p,
            formationPlaceId, // ID of the slot if in starting XI
            subPosition,       // Position string if a sub
            number,            // Player number
            isStartingXI: !!xiPlayer,
            isSubstitute: !!subPlayer,
        };
    });
     const listPlayersDataTwo = allTeamPlayersTwo.map(p => {
        const xiPlayer = sheetPlayersTwo.find(sp => sp.wyId === p.wyId);
        const subPlayer = subsTwo.find(sp => sp.wyId === p.wyId);
        const formationPlaceId = slotMapTwo[p.wyId] ?? null;
        const subPosition = subPlayer ? subPlayer.position : null;
        const number = xiPlayer?.number || subPlayer?.number || p.number || null;

        return {
            ...p,
            formationPlaceId,
            subPosition,
            number,
            isStartingXI: !!xiPlayer,
            isSubstitute: !!subPlayer,
        };
    });

    // Essential data check before rendering
    if (!teamOne || !teamTwo || !formationOneInfo || !formationTwoInfo) {
         console.error("Essential data missing for rendering:", { teamOne, teamTwo, formationOneInfo, formationTwoInfo });
         return <div>Essential match data (teams, formations) is missing.</div>;
    }

    return (
        <div className="page-match">
            <div className="wrapper">
                <div className="title-block">
                    <h1 className="teams-title">
                        <span className="team">
                            <span className="image">
                                {/* Use actual properties: logo, name */}
                                <TeamLogo logoUrl={teamOne.logo} teamName={teamOne.name} />
                            </span>
                            <span className="full-name">
                                {teamOne.name}
                            </span>
                        </span>
                        <span className="score">
                            {liveScore
                              ? `${liveScore.home} - ${liveScore.away}`
                              : `${match.homeScore ?? '-'} - ${match.awayScore ?? '-'}`}
                        </span>
                        <span className="team">
                            <span className="image">
                                <TeamLogo logoUrl={teamTwo.logo} teamName={teamTwo.name} />
                            </span>
                            <span className="full-name">
                                {teamTwo.name}
                            </span>
                        </span>
                    </h1>
                </div>

                <div className="match-date">
                    {/* Use actual date property and format it */}
                    {match.date ? new Date(match.date).toLocaleString() : 'Date unavailable'}
                </div>

                {/* Pass correct IDs */}
                <MatchPageNav id={match._id} teamOneId={teamOne._id} teamTwoId={teamTwo._id} isSummaryEnabled={match?.status === 'completed'} />

                {/* Lineup display */}
                <div className="lineup-grid">
                    {/* Render Home Team Lineup */}
                    {formationOneInfo && allTeamPlayersOne ? (
                        <div className="lineup-block">
                            <div className="field-block">
                                <FieldBlock
                                    formationInfo={formationOneInfo}
                                    teamSheetPlayers={sheetPlayersOne} // Pass players from teamsheet XI
                                    allTeamPlayers={allTeamPlayersOne} // Pass full roster details
                                    color={teamOne.kitColor || '#0040FF'}
                                />
                            </div>
                            <ListPlayers
                                formationInfo={formationOneInfo}
                                teamPlayers={listPlayersDataOne} // Pass the combined list
                            />
                        </div>
                    ) : (
                        <div className="lineup-block" style={{ textAlign: 'center', padding: '20px' }}>
                            Loading Home Team Lineup...
                        </div>
                    )}

                    {/* Render Away Team Lineup */}
                    {formationTwoInfo && allTeamPlayersTwo ? (
                        <div className="lineup-block">
                            <div className="field-block">
                                <FieldBlock
                                    formationInfo={formationTwoInfo}
                                    teamSheetPlayers={sheetPlayersTwo} // Pass players from teamsheet XI
                                    allTeamPlayers={allTeamPlayersTwo} // Pass full roster details
                                    color={teamTwo.kitColor || '#CC0E13'}
                                />
                            </div>
                            <ListPlayers
                                formationInfo={formationTwoInfo}
                                teamPlayers={listPlayersDataTwo} // Pass the combined list
                            />
                        </div>
                     ) : (
                         <div className="lineup-block" style={{ textAlign: 'center', padding: '20px' }}>
                             Loading Away Team Lineup...
                         </div>
                     )}
                 </div>
 
                 <div className="back-link">
                <Link to={match.league?.slug ? `/${match.league.slug}/fixtures` : (activeLeague ? `/${activeLeague.slug}/fixtures` : '/matches')} className="read-more-btn">
                     <span className="ico icon-arrow-left" />
                     BACK TO FIXTURES
                </Link>
            </div>
         </div>
     </div>
    )
}

export {MatchLineups}
